import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["src", "alt"]
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = ["src", "alt"]

import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { debug as d } from "debug";
import cms from "../../services/cms/cms";
import {
  CmsMediaLink,
  CmsQuote,
  CmsQuoteEmbed,
  CmsQuoteHorizontalBarPosition,
  CmsQuoteImagePosition,
} from "../../services/cms/cms-type-definitions";
import RenderWaiter from "../../services/utilities/render-waiter";


export default /*#__PURE__*/_defineComponent({
  __name: 'cms-quote',
  props: {
    slice: {},
    renderWaiter: {}
  },
  setup(__props: any) {

const debug = d("cs.CmsQuoteComponent");
const props = __props;

const quoteImagePositionCssClassMap = [
  CmsQuoteImagePosition.left,
  CmsQuoteImagePosition.right,
  CmsQuoteImagePosition.bottom,
].reduce((theMap, enumValue) => {
  theMap[enumValue] = `quote-image-${enumValue}`;
  return theMap;
}, {});
const quoteHorizontalBarPositionCssClassMap = [
  CmsQuoteHorizontalBarPosition.top,
  CmsQuoteHorizontalBarPosition.bottom,
].reduce((theMap, enumValue) => {
  theMap[enumValue] = `quote-bar-at-${enumValue}`;
  return theMap;
}, {});

// template refs
const quoteDiv = ref<HTMLDivElement | null>(null);
const imageAtTop = ref<HTMLImageElement | null>(null);
const imageAtBottom = ref<HTMLImageElement | null>(null);

const quoteDoc = ref<CmsQuote | null>(null);
const cssClasses = ref({});
const quoteText = ref("");
const quoteSource = ref("");
const imageSrc = ref("");
const imageAlternateText = ref("");
const isImageAtBottom = ref(false);

const imageSrcForShapeOutside = computed(() => {
  return `${imageSrc.value || ""}?t=${Date.now().toString()}`;
});

const imageElement = computed(() => {
  return isImageAtBottom.value ? imageAtBottom.value : imageAtTop.value;
});

function createCssClassesObject(
  imagePosition: CmsQuoteImagePosition,
  barPosition: CmsQuoteHorizontalBarPosition,
) {
  const cssClassesArray: Array<string> = [];
  if (quoteImagePositionCssClassMap[imagePosition]) {
    cssClassesArray.push(quoteImagePositionCssClassMap[imagePosition]);
  }
  if (quoteHorizontalBarPositionCssClassMap[barPosition]) {
    cssClassesArray.push(quoteHorizontalBarPositionCssClassMap[barPosition]);
  }
  const cssClassesObject = cssClassesArray.reduce((classesObj, className) => {
    classesObj[className] = true;
    return classesObj;
  }, {});
  return cssClassesObject;
}

function deferImageTextWrapping() {
  setTimeout(() => {
    if (props.renderWaiter) {
      props.renderWaiter.notifyReady(undefined);
    }
  }, 0);
}

function setImageWidthAndTextWrapping(image: CmsMediaLink) {
  const rawImageWidthPx = image?.width ?? Number.NaN;
  const widthOfDiv = quoteDiv.value?.clientWidth || 1440;
  const imageElementWidth = !Number.isNaN(rawImageWidthPx)
    ? `${(rawImageWidthPx / 1440) * widthOfDiv}px`
    : "100%";
  const imgElement = imageElement.value as HTMLElement;
  imgElement.style.width = imageElementWidth;

  deferImageTextWrapping();
}

function onWindowResize() {
  debug("onWindowResize");
  if (quoteDoc.value && quoteDoc.value.data.image) {
    setImageWidthAndTextWrapping(quoteDoc.value.data.image);
  }
}

onMounted(() => {
  debug("mounted");
  if (props.slice && props.slice.primary.document && props.slice.primary.document.id) {
    if (props.renderWaiter) {
      props.renderWaiter.waitFor(undefined);
    }
    cms.getQuote(props.slice.primary.document.id).then((quoteDocResponse) => {
      debug("Got quote doc", quoteDocResponse);
      quoteDoc.value = quoteDocResponse as CmsQuote;
      const properties = quoteDoc.value.data;
      quoteText.value = cms.asHtml(properties.content);
      quoteSource.value = cms.asHtml(properties.source);

      if (properties.image_position !== CmsQuoteImagePosition.none && properties.image.url) {
        imageSrc.value = "";
        imageAlternateText.value = "Image to accompany quote";
        isImageAtBottom.value = properties.image_position === CmsQuoteImagePosition.bottom;

        setTimeout(() => {
          imageSrc.value = properties.image.url;
          const imgElement = imageElement.value as HTMLElement;
          imgElement.onload = () => {
            setImageWidthAndTextWrapping(properties.image);
            window.addEventListener("resize", onWindowResize);
          };
        }, 0);
      } else {
        props.renderWaiter.notifyReady(undefined);
      }

      cssClasses.value = createCssClassesObject(
        properties.image_position,
        properties.horizontal_bar_position,
      );
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onWindowResize);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "quoteDiv",
    ref: quoteDiv,
    class: _normalizeClass(["quote", cssClasses.value])
  }, [
    (!isImageAtBottom.value)
      ? (_openBlock(), _createElementBlock("img", {
          key: 0,
          ref_key: "imageAtTop",
          ref: imageAtTop,
          src: imageSrc.value,
          alt: imageAlternateText.value,
          class: "quote-image",
          style: _normalizeStyle({ 'shape-outside': 'url(' + imageSrcForShapeOutside.value + ')' })
        }, null, 12, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: "quote-text",
      innerHTML: quoteText.value
    }, null, 8, _hoisted_2),
    _createTextVNode(" - "),
    _createElementVNode("div", {
      class: "quote-source",
      innerHTML: quoteSource.value
    }, null, 8, _hoisted_3),
    (isImageAtBottom.value)
      ? (_openBlock(), _createElementBlock("img", {
          key: 1,
          ref_key: "imageAtBottom",
          ref: imageAtBottom,
          src: imageSrc.value,
          alt: imageAlternateText.value,
          class: "quote-image",
          style: _normalizeStyle({ 'shape-outside': 'url(' + imageSrcForShapeOutside.value + ')' })
        }, null, 12, _hoisted_4))
      : _createCommentVNode("", true)
  ], 2))
}
}

})